import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    GraduationCap, ArrowRight, Sparkles, MessagesSquare, Layers, ClipboardCheck,
    Timer, Target, Gauge, BarChart3, Upload, Brain, Repeat, Rocket, BookOpen,
    Highlighter, AlarmClock, Stethoscope, Scale, Briefcase, Users, Check, X,
    Quote, PlayCircle, Zap, Gift, HandHeart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/context/LanguageContext";
import { LanguageSwitcher } from "@/components/common/LanguageSwitcher";
import { AnimatedJourney } from "@/components/landing/AnimatedJourney";
import { InteractiveProductTour } from "@/components/landing/InteractiveProductTour";

const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
};

const navIds = ["features", "modes", "howItWorks", "pricing", "rewards", "tour"];
const navTargets = { features: "features", modes: "reading-modes", howItWorks: "how-it-works", rewards: "ambassador", tour: "product-tour" };

const featureIcons = [GraduationCap, Sparkles, MessagesSquare, Layers, ClipboardCheck, Timer, Target, Brain, Rocket, BarChart3, Gauge, Zap];
const featureKeys = ["ai-tutor", "ai-summaries", "chat-with-documents", "flashcards", "quiz-generator", "exam-mode", "knowledge-gap-detection", "knowledge-extraction", "ai-coach", "analytics", "reading-modes", "speed-reading"];
const readingModeIcons = [Zap, Gauge, Target];
const readingModeKeys = ["rsvp", "ticker", "focus"];
const howIcons = [Upload, Sparkles, Layers, GraduationCap];
const problemIcons = [BookOpen, Highlighter, AlarmClock];
const pillarIcons = [Brain, Repeat, Rocket];
const personaIcons = [GraduationCap, Target, Briefcase, BookOpen];
const testimonialKeys = ["student", "medical-student", "law-student", "entrepreneur", "professional"];

const fadeUp = {
    initial: { opacity: 0, y: 12 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
};

const SectionLabel = ({ children }) => (
    <div className="mono-label mb-3 text-[hsl(var(--primary))]">{children}</div>
);

export default function Landing() {
    const { user } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    React.useEffect(() => { if (user) navigate("/library"); }, [user, navigate]);

    const journeyChips = t("landing.journey");
    const features = t("landing.features.items");
    const personas = t("landing.personas.items");
    const howSteps = t("landing.how.steps");
    const problemCards = t("landing.problem.cards");
    const pillars = t("landing.solution.pillars");
    const tradBullets = t("landing.comparison.traditionalBullets");
    const losBullets = t("landing.comparison.learnosBullets");
    const testimonials = t("landing.social.testimonials");
    const readingModes = t("landing.readingModes.modes");
    const heroModes = t("landing.heroModes");
    const positioning = t("landing.comparison.positioning");

    return (
        <div className="min-h-screen overflow-x-hidden bg-surface-0 text-t1">
            {/* Nav */}
            <header className="sticky top-0 z-50 border-b border-border bg-surface-0/80 backdrop-blur supports-[backdrop-filter]:bg-surface-0/60">
                <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
                    <div className="flex items-baseline gap-3">
                        <div className="flex items-center gap-2">
                            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">
                                <GraduationCap className="h-5 w-5" />
                            </div>
                            <span className="font-sans-display text-base font-semibold tracking-tight text-t1">LearnOS</span>
                        </div>
                        <span className="mono-label hidden text-t4 sm:inline">{t("nav.brandTag")}</span>
                    </div>

                    <nav className="hidden items-center gap-5 lg:flex">
                        {navIds.map((id) => (
                            id === "pricing" ? (
                                <Link
                                    key={id}
                                    to="/pricing"
                                    data-testid="nav-link-pricing"
                                    className="border-b border-transparent pb-0.5 text-sm font-medium text-t2 transition-colors hover:border-[hsl(var(--primary))] hover:text-t1"
                                >
                                    {t("nav.pricing")}
                                </Link>
                            ) : (
                                <a
                                    key={id}
                                    href={`#${navTargets[id]}`}
                                    data-testid={`nav-link-${navTargets[id]}`}
                                    className="border-b border-transparent pb-0.5 text-sm text-t3 transition-colors hover:border-[hsl(var(--primary))] hover:text-t1"
                                >
                                    {t(`nav.${id}`)}
                                </a>
                            )
                        ))}
                    </nav>

                    <div className="flex items-center gap-1.5">
                        <LanguageSwitcher testId="language-switcher-header" />
                        <Link to="/login"><Button data-testid="nav-sign-in-link" variant="ghost" className="text-t2">{t("common.signIn")}</Button></Link>
                        <Link to="/register"><Button data-testid="nav-start-learning-free-button" className="gap-1.5">{t("common.startFree")} <ArrowRight className="h-4 w-4" /></Button></Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative overflow-hidden hero-wash">
                <div className="noise-overlay pointer-events-none absolute inset-0" />
                <div className="relative mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 px-4 py-14 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-22">
                    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className="lg:col-span-5">
                        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-border bg-surface-2 px-3 py-1.5">
                            <Zap className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />
                            <span className="mono-label text-t3">{t("landing.heroBadge")}</span>
                        </div>
                        <h1 className="font-sans-display text-4xl font-semibold tracking-tight text-t1 sm:text-5xl lg:text-6xl">
                            {t("landing.heroTitle1")}<br /><span className="text-[hsl(var(--primary))]">{t("landing.heroTitle2")}</span>
                        </h1>
                        <p className="mt-4 max-w-md text-base leading-relaxed text-t2 md:text-lg">{t("landing.heroSubtitle")}</p>
                        <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                            <Link to="/register"><Button data-testid="hero-start-learning-free-button" size="lg" className="w-full gap-2 sm:w-auto">{t("common.startFree")} <ArrowRight className="h-4 w-4" /></Button></Link>
                            <Button data-testid="hero-watch-demo-button" size="lg" variant="secondary" className="w-full gap-2 bg-surface-3/60 sm:w-auto" onClick={() => scrollToId("product-tour")}>
                                <PlayCircle className="h-4 w-4" /> {t("common.watchDemo")}
                            </Button>
                        </div>
                        <div className="mt-7 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-t3">
                            <span className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-[hsl(var(--success))]" /> {t("landing.trust1")}</span>
                            <span className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-[hsl(var(--success))]" /> {t("landing.trust2")}</span>
                            <span className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-[hsl(var(--success))]" /> {t("landing.trust3")}</span>
                        </div>

                        {/* Reading Modes teaser — a real differentiator, surfaced in the hero */}
                        <div className="mt-7" data-testid="hero-reading-modes">
                            <div className="mono-label mb-2 text-t4">{t("landing.heroModesLabel")}</div>
                            <div className="grid grid-cols-3 gap-2">
                                {heroModes.map((m, idx) => {
                                    const Icon = [Zap, Gauge, Target][idx] || Zap;
                                    return (
                                        <div key={idx} data-testid={`hero-mode-${idx}`} className="rounded-xl border border-border bg-surface-2/70 px-3 py-2.5 backdrop-blur">
                                            <Icon className="h-4 w-4 text-[hsl(var(--primary))]" />
                                            <div className="mt-1.5 text-xs font-semibold text-t1">{m.name}</div>
                                            <div className="text-[11px] leading-snug text-t4">{m.tag}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </motion.div>

                    <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="lg:col-span-7">
                        <AnimatedJourney />
                    </motion.div>
                </div>
            </section>

            {/* Problem */}
            <section id="problem" data-testid="problem-section" className="scroll-mt-24 bg-surface-0">
                <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-4 py-14 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-18">
                    <motion.div {...fadeUp} className="lg:col-span-5">
                        <SectionLabel>{t("landing.problem.label")}</SectionLabel>
                        <h2 className="font-sans-display text-2xl font-semibold tracking-tight text-t1 sm:text-3xl">{t("landing.problem.title")}</h2>
                        <p className="mt-4 font-reading text-lg leading-relaxed text-t2">{t("landing.problem.body")}</p>
                    </motion.div>
                    <motion.div {...fadeUp} className="grid gap-4 lg:col-span-7">
                        {problemCards.map((p, i) => {
                            const Icon = problemIcons[i];
                            return (
                                <div key={i} className="flex items-start gap-4 rounded-[var(--radius)] border border-border bg-surface-2 p-5 shadow-elev-1">
                                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-surface-3 text-t3"><Icon className="h-5 w-5" /></div>
                                    <div>
                                        <h3 className="font-sans-display text-base font-semibold text-t1">{p.title}</h3>
                                        <p className="mt-1 text-sm text-t3">{p.desc}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </motion.div>
                </div>
            </section>

            {/* Solution */}
            <section data-testid="solution-section" className="bg-surface-1">
                <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8 lg:py-18">
                    <motion.div {...fadeUp} className="max-w-2xl">
                        <SectionLabel>{t("landing.solution.label")}</SectionLabel>
                        <h2 className="font-sans-display text-2xl font-semibold tracking-tight text-t1 sm:text-3xl">{t("landing.solution.title")}</h2>
                        <p className="mt-3 text-base leading-relaxed text-t2">{t("landing.solution.body")}</p>
                    </motion.div>

                    <motion.div {...fadeUp} className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
                        {pillars.map((p, i) => {
                            const Icon = pillarIcons[i];
                            return (
                                <div key={i} className="rounded-[var(--radius)] border border-border bg-surface-2 p-5 shadow-elev-1">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]"><Icon className="h-5 w-5" /></div>
                                    <h3 className="mt-4 font-sans-display text-base font-semibold text-t1">{p.title}</h3>
                                    <p className="mt-1.5 text-sm leading-relaxed text-t3">{p.desc}</p>
                                </div>
                            );
                        })}
                    </motion.div>

                    <motion.div {...fadeUp} className="mt-8 flex flex-wrap items-center gap-2">
                        {journeyChips.map((c, i) => (
                            <React.Fragment key={c}>
                                <span className="rounded-full border border-border bg-surface-2 px-3 py-1.5 text-xs text-t2">{c}</span>
                                {i < journeyChips.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-t4" />}
                            </React.Fragment>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* How it works */}
            <section id="how-it-works" data-testid="how-it-works-section" className="scroll-mt-24 bg-surface-0">
                <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8 lg:py-18">
                    <motion.div {...fadeUp} className="mb-8 max-w-2xl">
                        <SectionLabel>{t("landing.how.label")}</SectionLabel>
                        <h2 className="font-sans-display text-2xl font-semibold tracking-tight text-t1 sm:text-3xl">{t("landing.how.title")}</h2>
                    </motion.div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {howSteps.map((s, idx) => {
                            const Icon = howIcons[idx];
                            return (
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, y: 12 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.45, delay: idx * 0.06, ease: [0.22, 1, 0.36, 1] }}
                                    className="group rounded-[var(--radius)] border border-border bg-surface-2 p-5 shadow-elev-1 transition-colors hover:border-[hsl(var(--primary))]/30"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface-3 text-[hsl(var(--primary))]"><Icon className="h-5 w-5" /></div>
                                        <Badge variant="secondary" className="bg-surface-3 font-mono-ui text-[10px] uppercase text-t3">{t("landing.how.step")} {idx + 1}</Badge>
                                    </div>
                                    <h3 className="mt-4 font-sans-display text-base font-semibold text-t1">{s.title}</h3>
                                    <p className="mt-1.5 text-sm leading-relaxed text-t3">{s.desc}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Feature grid */}
            <section id="features" data-testid="feature-grid-section" className="scroll-mt-24 bg-surface-1">
                <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8 lg:py-18">
                    <motion.div {...fadeUp} className="mb-8 max-w-2xl">
                        <SectionLabel>{t("landing.features.label")}</SectionLabel>
                        <h2 className="font-sans-display text-2xl font-semibold tracking-tight text-t1 sm:text-3xl">{t("landing.features.title")}</h2>
                        <p className="mt-3 text-base leading-relaxed text-t2">{t("landing.features.body")}</p>
                    </motion.div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 sm:gap-5">
                        {features.map((f, idx) => {
                            const Icon = featureIcons[idx];
                            return (
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, y: 12 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: Math.min(idx * 0.04, 0.25), ease: [0.22, 1, 0.36, 1] }}
                                    data-testid={`feature-card-${featureKeys[idx]}`}
                                    className="group rounded-[var(--radius)] border border-border bg-surface-2 p-5 shadow-elev-1 transition-colors hover:border-[hsl(var(--primary))]/35 hover:bg-surface-3"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface-3 text-[hsl(var(--primary))] group-hover:bg-surface-2"><Icon className="h-5 w-5" /></div>
                                        <span className="mono-label rounded-full border border-border bg-surface-1 px-2 py-0.5 text-t4">{t("common.included")}</span>
                                    </div>
                                    <h3 className="mt-4 font-sans-display text-base font-semibold text-t1">{f.title}</h3>
                                    <p className="mt-1.5 text-sm leading-relaxed text-t3">{f.desc}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Reading Modes */}
            <section id="reading-modes" data-testid="reading-modes-section" className="scroll-mt-24 bg-surface-0">
                <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8 lg:py-18">
                    <motion.div {...fadeUp} className="mb-8 max-w-2xl">
                        <SectionLabel>{t("landing.readingModes.label")}</SectionLabel>
                        <h2 className="font-sans-display text-2xl font-semibold tracking-tight text-t1 sm:text-3xl">{t("landing.readingModes.title")}</h2>
                        <p className="mt-3 text-base leading-relaxed text-t2">{t("landing.readingModes.body")}</p>
                    </motion.div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-5">
                        {readingModes.map((m, idx) => {
                            const Icon = readingModeIcons[idx] || Gauge;
                            return (
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, y: 12 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: idx * 0.06, ease: [0.22, 1, 0.36, 1] }}
                                    data-testid={`reading-mode-card-${readingModeKeys[idx]}`}
                                    className="group rounded-[var(--radius)] border border-border bg-surface-2 p-6 shadow-elev-1 transition-colors hover:border-[hsl(var(--primary))]/35"
                                >
                                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))]"><Icon className="h-5 w-5" /></div>
                                    <h3 className="mt-4 font-sans-display text-base font-semibold text-t1">{m.name}</h3>
                                    <p className="mt-1.5 text-sm leading-relaxed text-t3">{m.desc}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Target users */}
            <section data-testid="target-users-section" className="bg-surface-0">
                <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8 lg:py-18">
                    <motion.div {...fadeUp} className="mb-8 max-w-2xl">
                        <SectionLabel>{t("landing.personas.label")}</SectionLabel>
                        <h2 className="font-sans-display text-2xl font-semibold tracking-tight text-t1 sm:text-3xl">{t("landing.personas.title")}</h2>
                    </motion.div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {personas.map((p, idx) => {
                            const Icon = personaIcons[idx] || GraduationCap;
                            return (
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, y: 12 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: Math.min(idx * 0.05, 0.25), ease: [0.22, 1, 0.36, 1] }}
                                    data-testid={`persona-card-${idx}`}
                                    className="flex flex-col rounded-[var(--radius)] border border-border bg-surface-2 p-5 shadow-elev-1"
                                >
                                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))]"><Icon className="h-5 w-5" /></div>
                                    <h3 className="mt-4 font-sans-display text-base font-semibold text-t1">{p.title}</h3>
                                    <p className="mt-1.5 text-sm leading-relaxed text-t3">{p.desc}</p>
                                    {Array.isArray(p.tags) && (
                                        <div className="mt-3 flex flex-wrap gap-1.5">
                                            {p.tags.map((tag) => (
                                                <span key={tag} className="rounded-full border border-border bg-surface-1 px-2.5 py-1 text-[11px] font-medium text-t2">{tag}</span>
                                            ))}
                                        </div>
                                    )}
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Comparison */}
            <section data-testid="comparison-section" className="bg-surface-1">
                <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8 lg:py-18">
                    <motion.div {...fadeUp} className="mb-8 max-w-2xl">
                        <SectionLabel>{t("landing.comparison.label")}</SectionLabel>
                        <h2 className="font-sans-display text-2xl font-semibold tracking-tight text-t1 sm:text-3xl">{t("landing.comparison.title")}</h2>
                    </motion.div>
                    <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-12">
                        <motion.div {...fadeUp} className="rounded-[var(--radius)] border border-border bg-surface-2 p-6 shadow-elev-1 lg:col-span-5">
                            <div className="mono-label text-t4">{t("landing.comparison.traditional")}</div>
                            <div className="mt-2 font-sans-display text-lg font-semibold text-t2">{t("landing.comparison.traditionalFlow")}</div>
                            <div className="mt-5 space-y-2.5">
                                {tradBullets.map((b) => (
                                    <div key={b} className="flex items-center gap-2 text-sm text-t3"><X className="h-4 w-4 text-[hsl(var(--destructive))]" />{b}</div>
                                ))}
                            </div>
                        </motion.div>

                        <div className="hidden items-center justify-center lg:col-span-2 lg:flex">
                            <div className="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-surface-1 text-[hsl(var(--primary))]"><ArrowRight className="h-5 w-5" /></div>
                        </div>

                        <motion.div {...fadeUp} className="rounded-[var(--radius)] border border-[hsl(var(--primary))]/30 bg-surface-2 p-6 shadow-elev-1 lg:col-span-5">
                            <div className="mono-label text-[hsl(var(--primary))]">{t("landing.comparison.learnos")}</div>
                            <div className="mt-2 font-sans-display text-lg font-semibold text-t1">{t("landing.comparison.learnosFlow")}</div>
                            <div className="mt-5 space-y-2.5">
                                {losBullets.map((b) => (
                                    <div key={b} className="flex items-center gap-2 text-sm text-t2"><Check className="h-4 w-4 text-[hsl(var(--success))]" />{b}</div>
                                ))}
                            </div>
                        </motion.div>
                    </div>

                    {/* Positioning contrast — where LearnOS is strongest */}
                    <motion.div {...fadeUp} className="mt-6">
                        <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-3">
                            <span className="mono-label text-[hsl(var(--primary))]">{t("landing.comparison.positioningLabel")}</span>
                            <span className="font-sans-display text-base font-semibold text-t1 sm:text-lg">{t("landing.comparison.positioningTitle")}</span>
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {positioning.map((row, idx) => (
                                <div
                                    key={idx}
                                    data-testid={`positioning-row-${idx}`}
                                    className="flex items-center gap-3 rounded-[var(--radius)] border border-border bg-surface-2 px-4 py-3 shadow-elev-1"
                                >
                                    <span className="inline-flex items-center gap-1.5 text-sm text-t4 line-through decoration-[hsl(var(--destructive))]/50">
                                        <X className="h-3.5 w-3.5 text-[hsl(var(--destructive))]/70" />{row.from}
                                    </span>
                                    <ArrowRight className="h-4 w-4 shrink-0 text-t4" />
                                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-t1">
                                        <Check className="h-3.5 w-3.5 text-[hsl(var(--success))]" />{row.to}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Interactive Product Tour */}
            <section id="product-tour" data-testid="product-tour-section" className="scroll-mt-24 bg-surface-0">
                <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8 lg:py-18">
                    <motion.div {...fadeUp} className="mb-8 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
                        <div className="max-w-2xl">
                            <SectionLabel>{t("landing.tour.label")}</SectionLabel>
                            <h2 className="font-sans-display text-2xl font-semibold tracking-tight text-t1 sm:text-3xl">{t("landing.tour.title")}</h2>
                            <p className="mt-3 text-base leading-relaxed text-t2">{t("landing.tour.body")}</p>
                        </div>
                        <Link to="/register"><Button data-testid="product-tour-start-button" className="gap-2">{t("common.tryFree")} <ArrowRight className="h-4 w-4" /></Button></Link>
                    </motion.div>
                    <motion.div {...fadeUp}>
                        <InteractiveProductTour />
                    </motion.div>
                </div>
            </section>

            {/* Social proof */}
            <section id="testimonials" data-testid="social-proof-section" className="scroll-mt-24 bg-surface-1">
                <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8 lg:py-18">
                    <motion.div {...fadeUp} className="mb-8 flex items-center justify-between gap-4">
                        <div className="max-w-2xl">
                            <SectionLabel>{t("landing.social.label")}</SectionLabel>
                            <h2 className="font-sans-display text-2xl font-semibold tracking-tight text-t1 sm:text-3xl">{t("landing.social.title")}</h2>
                        </div>
                        <Badge variant="secondary" className="hidden shrink-0 bg-surface-3 font-mono-ui text-[10px] text-t4 sm:inline-flex">{t("common.placeholder")}</Badge>
                    </motion.div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {testimonials.map((tm, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: Math.min(idx * 0.05, 0.25), ease: [0.22, 1, 0.36, 1] }}
                                data-testid={`testimonial-card-${testimonialKeys[idx]}`}
                                className="rounded-[var(--radius)] border border-border bg-surface-2 p-5 shadow-elev-1"
                            >
                                <Quote className="h-5 w-5 text-[hsl(var(--primary))]" />
                                <p className="mt-3 font-reading text-sm leading-relaxed text-t1">“{tm.quote}”</p>
                                <div className="mt-4 flex items-center gap-3">
                                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-surface-3 font-sans-display text-xs font-semibold text-t1">{tm.role.slice(0, 1)}</div>
                                    <div>
                                        <div className="text-sm font-medium text-t1">{tm.role}</div>
                                        <div className="mono-label text-t4">{t("common.placeholder")}</div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Student Ambassador Program */}
            <section id="ambassador" data-testid="ambassador-program-section" className="scroll-mt-24 bg-surface-0">
                <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8 lg:py-18">
                    <motion.div {...fadeUp} className="relative overflow-hidden rounded-[var(--radius)] border border-[hsl(var(--primary))]/30 bg-surface-1 p-6 shadow-elev-2 sm:p-8 lg:p-10">
                        <div className="hero-wash pointer-events-none absolute inset-0 opacity-60" />
                        <div className="relative grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
                            <div className="lg:col-span-7">
                                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[hsl(var(--primary))]/35 bg-[hsl(var(--primary))]/10 px-3 py-1.5">
                                    <Gift className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />
                                    <span className="mono-label text-[hsl(var(--primary))]">{t("landing.ambassadorBlock.label")}</span>
                                </div>
                                <h2 className="font-sans-display text-2xl font-semibold tracking-tight text-t1 sm:text-3xl">{t("landing.ambassadorBlock.title")}</h2>
                                <p className="mt-3 max-w-xl text-base leading-relaxed text-t2">{t("landing.ambassadorBlock.body")}</p>
                                <div className="mt-6 flex flex-col gap-2.5">
                                    {["point1", "point2", "point3"].map((p) => (
                                        <div key={p} className="flex items-center gap-2.5 text-sm text-t2">
                                            <Check className="h-4 w-4 shrink-0 text-[hsl(var(--success))]" />
                                            {t(`landing.ambassadorBlock.${p}`)}
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-7">
                                    <Link to="/register">
                                        <Button data-testid="ambassador-program-cta" size="lg" className="gap-2">
                                            <HandHeart className="h-4 w-4" /> {t("landing.ambassadorBlock.cta")}
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                            <div className="hidden lg:col-span-5 lg:block">
                                <div className="rounded-[var(--radius)] border border-border bg-surface-2/70 p-6 shadow-elev-1 backdrop-blur">
                                    <div className="flex items-center justify-between">
                                        <span className="mono-label text-t4">{t("landing.ambassadorBlock.label")}</span>
                                        <Gift className="h-5 w-5 text-[hsl(var(--primary))]" />
                                    </div>
                                    <div className="mt-5 grid grid-cols-2 gap-3">
                                        <div className="rounded-xl bg-surface-3/60 p-3">
                                            <div className="font-sans-display text-2xl font-semibold text-t1">2 €</div>
                                            <div className="mono-label mt-1 text-t4">/ active member</div>
                                        </div>
                                        <div className="rounded-xl bg-surface-3/60 p-3">
                                            <div className="font-sans-display text-2xl font-semibold text-[hsl(var(--primary))]">+ free</div>
                                            <div className="mono-label mt-1 text-t4">Pro & Premium</div>
                                        </div>
                                    </div>
                                    <div className="mt-3 flex items-center gap-2 rounded-xl border border-border bg-surface-1 px-3 py-2.5">
                                        <Users className="h-4 w-4 text-[hsl(var(--primary))]" />
                                        <span className="text-xs text-t3">5 → Pro · 10 → Premium</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Final CTA */}
            <section data-testid="final-cta-section" className="relative overflow-hidden bg-surface-0">
                <div className="reader-vignette noise-overlay pointer-events-none absolute inset-0" />
                <div className="relative mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8 lg:py-18">
                    <div className="rounded-[var(--radius)] border border-border bg-surface-1 p-6 shadow-elev-2 sm:p-8">
                        <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
                            <div className="max-w-xl">
                                <h2 className="font-sans-display text-2xl font-semibold tracking-tight text-t1 sm:text-3xl">{t("landing.finalCta.title")}</h2>
                                <p className="mt-2 text-base leading-relaxed text-t2">{t("landing.finalCta.body")}</p>
                            </div>
                            <div className="flex flex-col gap-3 sm:flex-row">
                                <Link to="/register"><Button data-testid="final-cta-start-learning-free-button" size="lg" className="w-full gap-2 sm:w-auto">{t("common.startFree")} <ArrowRight className="h-4 w-4" /></Button></Link>
                                <Button data-testid="final-cta-see-in-action-button" size="lg" variant="secondary" className="w-full gap-2 bg-surface-3/60 sm:w-auto" onClick={() => scrollToId("product-tour")}>
                                    <PlayCircle className="h-4 w-4" /> {t("common.seeInAction")}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer data-testid="footer-section" className="border-t border-border bg-surface-0">
                <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-4 py-10 text-sm text-t3 sm:flex-row sm:px-6 lg:px-8">
                    <div className="flex items-center gap-2">
                        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]"><GraduationCap className="h-4 w-4" /></div>
                        <span className="font-sans-display font-semibold text-t1">LearnOS</span>
                    </div>
                    <span className="mono-label text-t4">{t("landing.footer.tagline")}</span>
                </div>
            </footer>
        </div>
    );
}
